import { Injectable } from '@angular/core';
import { AuthResponseData, InvestorExtndListData, RefreshAuthResponseData, InvestorStore } from '../models/general/auth-response-vm';
import { IndexedDBAngular } from 'indexeddb-angular';
import { ToastController, ToastOptions, Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { predefinedDate, refreshData } from '../models/account/login-vm';
import { InvestorModel } from '../models/investor/investor-vm';
import { MfReportModel } from '../models/report/MfReportModel';
import { format, parseISO } from 'date-fns';
import { StorageService } from './core/storage.service';
import { Subject } from 'rxjs';
import { App } from '@capacitor/app';

const DATA_VERSION_KEY = 'APP_DATA_VERSION';

export interface BiometricState {
  isEnabled: boolean;           // User has enabled biometric login
  secretStored: boolean;        // Biometric secret successfully registered
  lastSuccessMessage: string;   // Last auth result ('my-super-secret' = success)
  isDismissed: boolean;         // User dismissed the prompt for the current session
  isInvoking: boolean;          // Currently showing biometric prompt
  iosFaceIdEnabled: boolean;    // iOS specific flag
  isSkipped: boolean;           // User explicitly skipped biometric setup (New!)
}

@Injectable({
  providedIn: 'root'
})

export class IdbService {
  private portfolioRefresh$ = new Subject<void>();

  triggerPortfolioRefresh() {
    this.portfolioRefresh$.next();
  }

  onPortfolioRefresh() {
    return this.portfolioRefresh$.asObservable();
  }
  private idb_service: any;
  private dbName = 'fc-db';
  private versionKey = 'APP_VERSION';
  public DATA_VERSION_KEY = 'APP_DATA_VERSION';

  private cachedAccessToken: string | null = null;
  private cachedRefreshToken: any = null;
  private cachedLastRefreshedTime: number | null = null;

  constructor(private platform: Platform, private toast_controller: ToastController, private storageService: StorageService) {
    const dbVersion = 13;
    this.idb_service = new IndexedDBAngular(this.dbName, dbVersion);
    this.idb_service.createStore(dbVersion, (db) => this.createCollections(db));
    
    // 🛡️ Automatic Update Detection
    this.platformReadyCheck();
  }

  private async platformReadyCheck() {
    await this.platform.ready();
    
    try {
      const info = await App.getInfo();
      // Combine version and build for a more reliable unique identifier (e.g., "20.4-41")
      const versionId = `${info.version}-${info.build}`;
      await this.checkForUpdate(versionId);
    } catch (e) {
      // If plugin fails, we check if we're actually on a browser
      if (Capacitor.getPlatform() === 'web') {
        await this.checkForUpdate('WEB_VERSION');
      } else {
        // On native, if App.getInfo fails, we skip checking rather than fallback to WEB_VERSION
        // to prevent accidental wipes due to plugin initialization delays.
        console.warn('IdbService: App.getInfo failed on native. Skipping version check to prevent accidental wipe.');
      }
    }
  }

  async checkForUpdate(currentVersion: string) {
    const storedVersion = localStorage.getItem(DATA_VERSION_KEY);
    
    // If no version stored, this is a fresh install or we just added this logic.
    // We set the version and DON'T wipe.
    if (!storedVersion) {
      localStorage.setItem(DATA_VERSION_KEY, currentVersion);
      return;
    }

    if (storedVersion !== currentVersion) {
      // 🚨 Version Mismatch detected (Actual Update)
      // We only wipe if the previous version wasn't 'WEB_VERSION' (to prevent plugin init issues)
      if (storedVersion !== 'WEB_VERSION') {
        await this.clearAllStoresForUpgrade();
        sessionStorage.clear();
        localStorage.clear();

        // 🛡️ SUPPLEMENTAL: Clear standard Cache Storage
        if (window.caches) {
          try {
            const keys = await window.caches.keys();
            for (const key of keys) {
              await window.caches.delete(key);
            }
          } catch (e) {
            console.warn('IdbService: Failed to clear Web Cache Storage', e);
          }
        }
      }
      
      // Update to the new version
      localStorage.setItem(DATA_VERSION_KEY, currentVersion);
    }
  }

  async clearAllStoresForUpgrade() {
    const stores = [
      'CURRENT_INVESTOR_STORE', 'ACCESS_TOKEN_STORE', 'USER_TYPE_STORE',
      'RECOMMENDATION_CATEGORY_ID', 'AUTH_DATA_STORE', 'REFRESH_TOKEN_STORE',
      'OTP_KEY_STORE', 'INVESTOR_LIST_STORE', 'FORCE_REFRESH_PAGE_STORE',
      'PREDEFINED_DATE_STORE', 'INVESTOR_FUND_DETAIL_DATA_STORE',
      'INVESTOR_STORE', 'SUCCESS_MESSAGE_STORE', 'ERRROR_MESSAGE_STORE',
      'SCHEME_LIST_STORE', 'HAS_SINGLE_INVESTOR_STORE', 'CURRENT_PAGE_STORE',
      'TRANSACTION_TYPE_STORE', 'PREVIOUS_PAGE_STORE', 'AUTHENTICATION_STORE',
      'IS_SSO_INVESTOR_STORE', '2FA_DETAIL_STORE'
    ];
    for (const storeName of stores) {
      try { 
        await this.idb_service.clear(storeName); 
      } catch (e) { }
    }
    await this.resetBiometricState();
    this.storageService.clearLoginCred();
  }

  async clearIndexedDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.deleteDatabase(this.dbName);
      request.onsuccess = () => {
        //console.log('IndexedDB deleted successfully');
        resolve(true);
      };
      request.onerror = () => {
        console.error('Failed to delete IndexedDB');
        reject(false);
      };
    });
  }

  createCollections(db) {
    db.currentTarget.result.createObjectStore('CURRENT_INVESTOR_STORE');
    db.currentTarget.result.createObjectStore('ACCESS_TOKEN_STORE');
    db.currentTarget.result.createObjectStore('USER_TYPE_STORE');
    db.currentTarget.result.createObjectStore('RECOMMENDATION_CATEGORY_ID');
    db.currentTarget.result.createObjectStore('AUTH_DATA_STORE');
    db.currentTarget.result.createObjectStore('REFRESH_TOKEN_STORE');
    db.currentTarget.result.createObjectStore('OTP_KEY_STORE');
    db.currentTarget.result.createObjectStore('INVESTOR_LIST_STORE');
    db.currentTarget.result.createObjectStore('FORCE_REFRESH_PAGE_STORE');
    db.currentTarget.result.createObjectStore('PREDEFINED_DATE_STORE');
    db.currentTarget.result.createObjectStore('INVESTOR_FUND_DETAIL_DATA_STORE');
    db.currentTarget.result.createObjectStore('INVESTOR_STORE');
    db.currentTarget.result.createObjectStore('SUCCESS_MESSAGE_STORE');
    db.currentTarget.result.createObjectStore('ERRROR_MESSAGE_STORE');
    db.currentTarget.result.createObjectStore('SCHEME_LIST_STORE');
    db.currentTarget.result.createObjectStore('HAS_SINGLE_INVESTOR_STORE');
    db.currentTarget.result.createObjectStore('CURRENT_PAGE_STORE');
    db.currentTarget.result.createObjectStore('TRANSACTION_TYPE_STORE');
    db.currentTarget.result.createObjectStore('PREVIOUS_PAGE_STORE');
    db.currentTarget.result.createObjectStore('AUTHENTICATION_STORE');
    db.currentTarget.result.createObjectStore('IS_SSO_INVESTOR_STORE');
    db.currentTarget.result.createObjectStore('2FA_DETAIL_STORE');
    db.currentTarget.result.createObjectStore('LOGIN_PREF_STORE');
  }

  async getByKey(store_name: string, key: string) {
    return await this.idb_service.getByKey(store_name, key);
  }

  async delete(store_name: string, key: string) {
    return await this.idb_service.delete(store_name, key);
  }

  async addValueToStore(store_name: string, value: any, key: string) {
    await this.idb_service.delete(store_name, key);
    await this.idb_service.add(store_name, value, key);
  }

  async setLoginTokenData(loginResponse: any) {
    const accessToken = loginResponse.AccessToken || loginResponse.aToken || loginResponse.access_token;
    this.cachedAccessToken = accessToken || "";
    await this.addValueToStore('ACCESS_TOKEN_STORE', accessToken || "", "ACCESS_TOKEN");
    
    // 🛡️ Strictly extract 90-Day RefreshToken JWT string and store it under 'RefreshToken'
    const refreshToken = loginResponse.RefreshToken || loginResponse.rToken || "";
    this.cachedRefreshToken = { RefreshToken: refreshToken };
    await this.addValueToStore('REFRESH_TOKEN_STORE', { RefreshToken: refreshToken }, "REFRESH_TOKEN");

    // 🛡️ Initialize last refreshed time on fresh login
    const now = Date.now();
    this.cachedLastRefreshedTime = now;
    await this.saveLastRefreshedTime(now);
  }

  async refreshLoginTokenData(loginResponse: any) {
    const accessToken = loginResponse.AccessToken || loginResponse.aToken || loginResponse.access_token;
    this.cachedAccessToken = accessToken || "";
    await this.addValueToStore('ACCESS_TOKEN_STORE', accessToken || "", "ACCESS_TOKEN");
    
    // 🛡️ Strictly extract 90-Day RefreshToken JWT string and store it under 'RefreshToken'
    const refreshToken = loginResponse.RefreshToken || loginResponse.rToken || "";
    this.cachedRefreshToken = { RefreshToken: refreshToken };
    await this.addValueToStore('REFRESH_TOKEN_STORE', { RefreshToken: refreshToken }, "REFRESH_TOKEN");

    // 🛡️ Update last refreshed time on token refresh
    const now = Date.now();
    this.cachedLastRefreshedTime = now;
    await this.saveLastRefreshedTime(now);
  }

  async setCurrentInvestor(_current_investor: InvestorExtndListData) {
    await this.addValueToStore('CURRENT_INVESTOR_STORE', _current_investor, "CURRENT_INVESTOR");
  }

  async setInvestor(_current_investor: InvestorExtndListData) {
    await this.addValueToStore('INVESTOR_STORE', _current_investor, "INVESTOR");
  }

  async getCurrentInvestorId() {
    let curr_investor = await this.getByKey('CURRENT_INVESTOR_STORE', "CURRENT_INVESTOR");
    return curr_investor.id;
  }

  async getCurrentInvestor(): Promise<InvestorExtndListData> {
    let curr_investor = await this.getByKey('CURRENT_INVESTOR_STORE', "CURRENT_INVESTOR");
    return curr_investor;
  }

  async getInvestor(): Promise<InvestorExtndListData> {
    let curr_investor = await this.getByKey('INVESTOR_STORE', "INVESTOR");
    return curr_investor;
  }

  async saveOtpKey(otpKey: string) {
    await this.addValueToStore('OTP_KEY_STORE', otpKey, "OTP_KEY");
  }

  async getOtpKey() {
    let otp_key = await this.getByKey('OTP_KEY_STORE', "OTP_KEY");
    return otp_key;
  }

  async setHasSingleInvestor(single_investor: boolean) {
    await this.addValueToStore('HAS_SINGLE_INVESTOR_STORE', single_investor, "SINGLE_INVESTOR");
  }

  async getHasSingleInvestor() {
    let single_investor = await this.getByKey('HAS_SINGLE_INVESTOR_STORE', "SINGLE_INVESTOR");
    return single_investor;
  }

  async setIsSSOInvestor(sso_investor: boolean) {
    await this.addValueToStore('IS_SSO_INVESTOR_STORE', sso_investor, "SSO_INVESTOR");
  }

  async getIsSSOInvestor() {
    let sso_investor = await this.getByKey('IS_SSO_INVESTOR_STORE', "SSO_INVESTOR");
    return sso_investor;
  }

  async setCurrentPage(currentPage: string) {
    await this.addValueToStore('CURRENT_PAGE_STORE', currentPage, "CURRENT_PAGE");
  }

  async getCurrentPage() {
    let current_page = await this.getByKey('CURRENT_PAGE_STORE', "CURRENT_PAGE");
    return current_page;
  }

  async setTranType(trantype: string) {
    await this.addValueToStore('TRANSACTION_TYPE_STORE', trantype, "TRANSACTION_TYPE");
  }

  async getTranType() {
    let trantype = await this.getByKey('TRANSACTION_TYPE_STORE', "TRANSACTION_TYPE");
    return trantype;
  }

  async setPrevtPage(currentPage: string) {
    await this.addValueToStore('PREVIOUS_PAGE_STORE', currentPage, "PREVIOUS_PAGE");
  }

  async getPrevPage() {
    let current_page = await this.getByKey('PREVIOUS_PAGE_STORE', "PREVIOUS_PAGE");
    return current_page;
  }

  async getLoginPref() {
    let keep_me_loggedin = await this.getByKey('LOGIN_PREF_STORE', 'KEEP_LOGGED_IN');
    return keep_me_loggedin;
  }

  async setScheme(scheme) {
    await this.addValueToStore('SCHEME_LIST_STORE', scheme, "SCHEME_STORE");
  }

  async getScheme() {
    let _scheme_list = await this.getByKey('SCHEME_LIST_STORE', "SCHEME_STORE");
    return _scheme_list;
  }

  async set2FADetail(details: []) {
    await this.addValueToStore('2FA_DETAIL_STORE', details, "2FA_STORE");
  }

  async get2FADetail() {
    let _detail = await this.getByKey('2FA_DETAIL_STORE', "2FA_STORE");
    return _detail;
  }

  async getTkn() {
    if (this.cachedAccessToken !== null) {
      return this.cachedAccessToken;
    }
    let _token = await this.getByKey('ACCESS_TOKEN_STORE', "ACCESS_TOKEN");
    this.cachedAccessToken = _token || "";
    return _token;
  }

  async getRTkn() {
    if (this.cachedRefreshToken !== null) {
      return this.cachedRefreshToken;
    }
    let _r_token = await this.getByKey('REFRESH_TOKEN_STORE', "REFRESH_TOKEN");
    this.cachedRefreshToken = _r_token;
    return _r_token;
  }

  // 🧪 DEBUG: Call this after deleting tokens from IndexedDB to also wipe the in-memory cache.
  // Without this, getRTkn() and getTkn() return stale cached values even after deletion.
  clearTokenCaches() {
    this.cachedAccessToken = null;
    this.cachedRefreshToken = null;
    this.cachedLastRefreshedTime = null;
  }

  async setInvestorList(_investor_list) {
    await this.addValueToStore('INVESTOR_LIST_STORE', _investor_list, "INVESTOR_LIST");
  }

  async getInvestorList() {
    let _investor_list = await this.getByKey('INVESTOR_LIST_STORE', "INVESTOR_LIST");
    return _investor_list;
  }

  async setInvestorFilterApplied(_refresh_page_list: refreshData) {
    await this.addValueToStore('FORCE_REFRESH_PAGE_STORE', _refresh_page_list, "FORCE_REFRESH_PAGE");
  }

  async getInvestorFilterApplied(): Promise<refreshData> {
    let _refresh_page_list: refreshData = await this.getByKey('FORCE_REFRESH_PAGE_STORE', "FORCE_REFRESH_PAGE");
    return _refresh_page_list;
  }

  async setPredefinedDateApplied(_predefined_date_page_list: predefinedDate) {
    await this.addValueToStore('PREDEFINED_DATE_STORE', _predefined_date_page_list, "PREDEFINED_DATE");
  }

  async getPredefinedDateApplied(): Promise<predefinedDate> {
    let _refresh_page_list: predefinedDate = await this.getByKey('PREDEFINED_DATE_STORE', "PREDEFINED_DATE");
    return _refresh_page_list;
  }

  async setInvestorFundDetail(_selected_fund_detail: any) {
    await this.addValueToStore('INVESTOR_FUND_DETAIL_DATA_STORE', _selected_fund_detail, "INVESTOR_FUND_DETAIL_DATA");
  }

  async getInvestorFundDetail() {
    let _selected_fund_detail = await this.getByKey('INVESTOR_FUND_DETAIL_DATA_STORE', "INVESTOR_FUND_DETAIL_DATA");
    return _selected_fund_detail;
  }

  async setInvestorFolioList(_investor_folio) {
    await this.addValueToStore('AUTH_DATA_STORE', _investor_folio, "FOLIO_LIST");
  }

  async getInvestorFolioList() {
    let _investor_folio = await this.getByKey('AUTH_DATA_STORE', "FOLIO_LIST");
    return _investor_folio;
  }

  async setCurrentUserType(_user_type) {
    await this.addValueToStore('USER_TYPE_STORE', _user_type, "USER_TYPE");
  }

  async getCurrentUserType() {
    let _user_type = await this.getByKey('USER_TYPE_STORE', "USER_TYPE");
    return _user_type;
  }

  async setRecommendationCategoryID(_recommendation_id) {
    await this.addValueToStore('RECOMMENDATION_CATEGORY_ID', _recommendation_id, "RECOMMENDATION_ID");
  }

  async getRecommendationCategoryID() {
    let _recommendation_id = await this.getByKey('RECOMMENDATION_CATEGORY_ID', "RECOMMENDATION_ID");
    return _recommendation_id;
  }

  async setSuccessMessage(_success_message) {
    await this.addValueToStore('SUCCESS_MESSAGE_STORE', _success_message, "SUCCESS_MESSAGE");
  }

  async getSuccessMessage() {
    let _success_message = await this.getByKey('SUCCESS_MESSAGE_STORE', "SUCCESS_MESSAGE");
    return _success_message;
  }

  async setErrorMessage(_error_message) {
    await this.addValueToStore('ERRROR_MESSAGE_STORE', _error_message, "ERROR_MESSAGE");
  }

  async getErrorMessage() {
    let _error_message = await this.getByKey('ERRROR_MESSAGE_STORE', "ERROR_MESSAGE");
    return _error_message;
  }

  async clearAllStores() {
    const stores = [
      'ACCESS_TOKEN_STORE',
      'USER_TYPE_STORE',
      'AUTH_DATA_STORE',
      'REFRESH_TOKEN_STORE',
      'OTP_KEY_STORE',
      'INVESTOR_LIST_STORE',
      'FORCE_REFRESH_PAGE_STORE',
      'PREDEFINED_DATE_STORE',
      'INVESTOR_FUND_DETAIL_DATA_STORE',
      'INVESTOR_STORE',
      'SUCCESS_MESSAGE_STORE',
      'ERRROR_MESSAGE_STORE',
      'SCHEME_LIST_STORE',
      'HAS_SINGLE_INVESTOR_STORE',
      'CURRENT_PAGE_STORE',
      'TRANSACTION_TYPE_STORE',
      'AUTHENTICATION_STORE',
      'IS_SSO_INVESTOR_STORE',
      '2FA_DETAIL_STORE',
      'PREVIOUS_PAGE_STORE',
      'CURRENT_INVESTOR_STORE',
      'LOGIN_PREF_STORE'
    ];

    const state = await this.getBiometricState();
    for (const storeName of stores) {
      // 🛡️ Always keep internal biometric settings, user type, and refresh timing
      if (storeName == 'AUTHENTICATION_STORE' || storeName == 'USER_TYPE_STORE') {
        continue;
      }

      // 🛡️ Shield tokens for biometric re-login ONLY if enabled
      if (state.isEnabled && (storeName === 'REFRESH_TOKEN_STORE' || storeName === 'ACCESS_TOKEN_STORE')) {
        continue;
      }

      try {
        await this.idb_service.clear(storeName);
      } catch (e) {
        console.warn(`IdbService: Failed to clear ${storeName}`, e);
      }
    }
  }

  async clearSessionDataOnly() {
    const storesToClear = [
      'OTP_KEY_STORE',
      'CURRENT_INVESTOR_STORE',
      'FORCE_REFRESH_PAGE_STORE',
      'PREDEFINED_DATE_STORE',
      'CURRENT_PAGE_STORE',
      'TRANSACTION_TYPE_STORE',
      'PREVIOUS_PAGE_STORE'
    ];

    for (const storeName of storesToClear) {
      try {
        await this.idb_service.clear(storeName);
      } catch (e) {
        console.warn(`IdbService: Failed to clear session store ${storeName}`, e);
      }
    }
    // 🛡️ Note: We intentionally DO NOT clear 'ACCESS_TOKEN_STORE', 'REFRESH_TOKEN_STORE', 
    // 'INVESTOR_STORE', or 'SCHEME_LIST_STORE' to allow Biometric re-authentication 
    // after logout or app kill.
  }

  async saveForDataLoadDsData(data: string) {
    await this.addValueToStore('AUTHENTICATION_STORE', data, "AUTHENTICATION_LOAD");
  }

  async getForDataLoadDsData() {
    let token = await this.getByKey('AUTHENTICATION_STORE', "AUTHENTICATION_LOAD");
    return token;
  }

  async saveDsInvokedData(data: boolean) {
    await this.addValueToStore('AUTHENTICATION_STORE', data, "AUTHENTICATION_INVOKE");
  }

  async getDsInvokedData() {
    let token = await this.getByKey('AUTHENTICATION_STORE', "AUTHENTICATION_INVOKE");
    return token;
  }

  async saveFlagForLoginDsBool(bool: boolean) {
    await this.addValueToStore('AUTHENTICATION_STORE', bool, "AUTHENTICATION_BOOL");
  }

  async getFlagForLoginDsBool() {
    let token = await this.getByKey('AUTHENTICATION_STORE', "AUTHENTICATION_BOOL");
    return token;
  }

  async saveDateTimeBool() {
    await this.addValueToStore('AUTHENTICATION_STORE', new Date(), "AUTHENTICATION_ACTIVEDATETIME");
  }

  async getDateTimeBool() {
    let token = await this.getByKey('AUTHENTICATION_STORE', "AUTHENTICATION_ACTIVEDATETIME");
    return token;
  }

  async saveFlagOfDsBool(bool: boolean) {
    await this.addValueToStore('AUTHENTICATION_STORE', bool, "AUTHENTICATION_FLAG_FINGERPRINT_BOOL");
  }

  async getFlagOfDsBool() {
    let token = await this.getByKey('AUTHENTICATION_STORE', "AUTHENTICATION_FLAG_FINGERPRINT_BOOL");
    return token;
  }

  async getLastLoginDateTime() {
    let _last_login_date_time = await this.getByKey('AUTHENTICATION_STORE', "AUTHENTICATION_LOGIN_DATE");
    return _last_login_date_time;
  }

  async saveLastLoginDateTime() {
    await this.addValueToStore('AUTHENTICATION_STORE', format(parseISO(new Date().toISOString()), "dd/MM/yyyy 'at' h:mm a").toLowerCase(), "AUTHENTICATION_LOGIN_DATE");
  }

  async saveDismissDsBool(bool: boolean) {
    await this.addValueToStore('AUTHENTICATION_STORE', bool, "AUTHENTICATION_FLAG_FINGERPRINT_DISMISS_BOOL");
  }

  async getDismissDsBool() {
    let token = await this.getByKey('AUTHENTICATION_STORE', "AUTHENTICATION_FLAG_FINGERPRINT_DISMISS_BOOL");
    return token;
  }

  async saveLoginNo(isFirstTImeLogin: number) {
    await this.addValueToStore('AUTHENTICATION_STORE', isFirstTImeLogin, "AUTHENTICATION_Is_FIRST_TIME_LOGIN_BOOL");
  }

  async getLoginNo() {
    let isFirstTImeLogin = await this.getByKey('AUTHENTICATION_STORE', "AUTHENTICATION_Is_FIRST_TIME_LOGIN_BOOL");
    return isFirstTImeLogin;
  }

  async saveLastActiveTime(time: number) {
    await this.addValueToStore('AUTHENTICATION_STORE', time, "LAST_ACTIVE_TIME");
  }

  async getLastActiveTime(): Promise<number> {
    const time = await this.getByKey('AUTHENTICATION_STORE', "LAST_ACTIVE_TIME");
    return time ? time : 0;
  }

  async saveLastRefreshedTime(time: number) {
    this.cachedLastRefreshedTime = time;
    await this.addValueToStore('AUTHENTICATION_STORE', time, "LAST_REFRESH_TIME");
  }

  async getLastRefreshedTime(): Promise<number> {
    if (this.cachedLastRefreshedTime !== null) {
      return this.cachedLastRefreshedTime;
    }
    const time = await this.getByKey('AUTHENTICATION_STORE', "LAST_REFRESH_TIME");
    this.cachedLastRefreshedTime = time ? time : 0;
    return this.cachedLastRefreshedTime;
  }

  async saveDsBool(isFirstTImeLoginBool: boolean) {
    await this.addValueToStore('AUTHENTICATION_STORE', isFirstTImeLoginBool, "AUTHENTICATION_IS_IOS_FACE_ID_ENABLED");

  }

  async getDsIdBool() {
    let isFaceIdBool = await this.getByKey('AUTHENTICATION_STORE', "AUTHENTICATION_IS_IOS_FACE_ID_ENABLED");
    return isFaceIdBool;
  }

  async getBiometricState(): Promise<BiometricState> {
    const [enabled, secret, dismissed, invoking, iosFaceId, skipped] = await Promise.all([
      this.getFlagOfDsBool(),
      this.getForDataLoadDsData(),
      this.getDismissDsBool(),
      this.getDsInvokedData(),
      this.getDsIdBool(),
      this.getBiometricSkipped()
    ]);

    return {
      isEnabled: enabled === true,
      secretStored: secret === 'my-super-secret',
      lastSuccessMessage: secret || '',
      isDismissed: dismissed === true,
      isInvoking: invoking === true,
      iosFaceIdEnabled: iosFaceId === true,
      isSkipped: skipped === true
    };
  }


  async setBiometricState(state: Partial<BiometricState>): Promise<void> {
    const saves: Promise<void>[] = [];
    if (state.isEnabled !== undefined) saves.push(this.saveFlagOfDsBool(state.isEnabled));
    if (state.secretStored !== undefined) saves.push(this.saveForDataLoadDsData(state.secretStored ? 'my-super-secret' : ''));
    if (state.isDismissed !== undefined) saves.push(this.saveDismissDsBool(state.isDismissed));
    if (state.isInvoking !== undefined) saves.push(this.saveDsInvokedData(state.isInvoking));
    if (state.iosFaceIdEnabled !== undefined) saves.push(this.saveDsBool(state.iosFaceIdEnabled));
    if (state.isSkipped !== undefined) saves.push(this.setBiometricSkipped(state.isSkipped));
    await Promise.all(saves);
  }

  async setBiometricSkipped(skipped: boolean) {
    await this.addValueToStore('AUTHENTICATION_STORE', skipped, "AUTHENTICATION_SKIPPED");
  }

  async getBiometricSkipped() {
    let skipped = await this.getByKey('AUTHENTICATION_STORE', "AUTHENTICATION_SKIPPED");
    return skipped;
  }

  async validateAndRecoverBiometricState(): Promise<boolean> {
    const state = await this.getBiometricState();
    const isInconsistent =
      (state.isEnabled && !state.secretStored) ||
      (!state.isEnabled && state.secretStored && state.lastSuccessMessage === 'my-super-secret') ||
      state.isInvoking;

    if (isInconsistent) {
      await this.resetBiometricState();
      return false;
    }
    return true;
  }

  async resetBiometricState(): Promise<void> {
    await this.setBiometricState({
      isEnabled: false,
      secretStored: false,
      isDismissed: false,
      isInvoking: false,
      iosFaceIdEnabled: false
    });
  }

  async deleteDatabase() {
    const stores = [
      'ACCESS_TOKEN_STORE',
      'USER_TYPE_STORE',
      'AUTH_DATA_STORE',
      'REFRESH_TOKEN_STORE',
      'OTP_KEY_STORE',
      'INVESTOR_LIST_STORE',
      'FORCE_REFRESH_PAGE_STORE',
      'PREDEFINED_DATE_STORE',
      'INVESTOR_FUND_DETAIL_DATA_STORE',
      'INVESTOR_STORE',
      'SUCCESS_MESSAGE_STORE',
      'ERRROR_MESSAGE_STORE',
      'SCHEME_LIST_STORE',
      'HAS_SINGLE_INVESTOR_STORE',
      'CURRENT_PAGE_STORE',
      'TRANSACTION_TYPE_STORE',
      'AUTHENTICATION_STORE',
      'IS_SSO_INVESTOR_STORE',
      '2FA_DETAIL_STORE'
    ];

    const state = await this.getBiometricState();
    for (const storeName of stores) {
      if (storeName == 'AUTHENTICATION_STORE' || storeName == 'USER_TYPE_STORE') continue;
      
      // If biometric is enabled, we preserve tokens and metadata
      if (state.isEnabled && (
          storeName === 'REFRESH_TOKEN_STORE' || 
          storeName === 'ACCESS_TOKEN_STORE' || 
          storeName === 'INVESTOR_STORE' || 
          storeName === 'SCHEME_LIST_STORE'
      )) {
          continue;
      }
      try {
        await this.idb_service.clear(storeName);
      } catch (e) {
        console.warn(`IdbService: Failed to clear store ${storeName} during db delete`, e);
      }
    }
  }

  /**
   * 🛡️ FULL WIPE for Identity Switching
   * Clears everything including biometrics to ensure new user starts fresh.
   */
  async wipeAllDataForNewUser() {
    const stores = [
      'CURRENT_INVESTOR_STORE',
      'ACCESS_TOKEN_STORE',
      'USER_TYPE_STORE',
      'RECOMMENDATION_CATEGORY_ID',
      'AUTH_DATA_STORE',
      'REFRESH_TOKEN_STORE',
      'OTP_KEY_STORE',
      'INVESTOR_LIST_STORE',
      'FORCE_REFRESH_PAGE_STORE',
      'PREDEFINED_DATE_STORE',
      'INVESTOR_FUND_DETAIL_DATA_STORE',
      'INVESTOR_STORE',
      'SUCCESS_MESSAGE_STORE',
      'ERRROR_MESSAGE_STORE',
      'SCHEME_LIST_STORE',
      'HAS_SINGLE_INVESTOR_STORE',
      'CURRENT_PAGE_STORE',
      'TRANSACTION_TYPE_STORE',
      'PREVIOUS_PAGE_STORE',
      'AUTHENTICATION_STORE',
      'IS_SSO_INVESTOR_STORE',
      '2FA_DETAIL_STORE',
      'LOGIN_PREF_STORE'
    ];

    for (const storeName of stores) {
      try {
        await this.idb_service.clear(storeName);
      } catch (e) {
        console.warn(`IdbService: Failed to clear ${storeName} during user wipe`, e);
      }
    }
    this.cachedAccessToken = null;
    this.cachedRefreshToken = null;
    this.cachedLastRefreshedTime = null;
    await this.resetBiometricState();
    this.storageService.clearLoginCred();
  }
}